<template>
  <div class="home">
    <div>
      <div class="row mb-4">
        <div class="col">
          <div class="row" v-if="editorMode">
            <section class="col-4">
              <h5>Views</h5>
              <label
                class="cstm-check dropdown-item d-flex align-items-center justify-content-between"
                v-for="d in allDashboards"
                :key="d.id ?? 'new'"
              >
                <div class="flex-grow-1">
                  <span v-if="dashboard?.id !== d.id" class="mx-2 regular-14">
                    {{ d.name }}
                  </span>
                  <input
                    v-else
                    type="text"
                    class="form-control form-control-sm mx-2"
                    v-model="d.name"
                  />
                </div>
                <input type="radio" v-model="dashboard" :value="d" />
                <span class="checkmark radio mx-2"></span>
              </label>
            </section>
            <section class="col-4">
              <h5>{{ $t("lineOptions.visibleFor") }}</h5>
              <label class="cstm-check dropdown-item">
                <span class="text-black mx-2 regular-14">{{
                  $t("lineOptions.allGroups")
                }}</span>
                <input
                  type="checkbox"
                  v-model="visible"
                  :value="0"
                  v-on:change="changeVisible(0)"
                /><span class="checkmark mx-2"></span>
              </label>
              <label
                class="cstm-check dropdown-item"
                v-for="role in roles"
                :key="role.id"
              >
                <span class="text-black mx-2 regular-14">{{ role.name }}</span>
                <input
                  type="checkbox"
                  v-model="visible"
                  :value="role.id"
                  v-on:change="
                    changeVisible(role.id, !$event.currentTarget.checked)
                  "
                /><span class="checkmark mx-2"></span>
              </label>
            </section>
          </div>
          <template v-else-if="dashboards.length > 1">
            <button
              v-for="(d, i) in dashboards"
              :key="d.id"
              :class="{
                'btn-no-l': i === dashboards.length - 1,
                'btn-no-r': i === 0,
                'btn-secondary': dashboard.id !== d.id,
                'btn-primary': dashboard.id === d.id,
              }"
              class="btn btn-small"
              @click="dashboard = d"
            >
              {{ d.name }}
            </button>
          </template>
        </div>
        <div class="col-auto" v-if="isAdmin">
          <button
            v-if="editorMode"
            class="btn btn-type-2 btn-small me-2"
            @click="saveLayout"
          >
            <span>Save Layout</span>
          </button>
          <button
            class="btn btn-type-4 btn-small"
            @click="editorMode = !editorMode"
          >
            <span>
              {{ editorMode ? "Exit Editor" : "Edit Layout" }}
            </span>
          </button>
        </div>
      </div>
    </div>
    <div class="row" :class="{ 'editor-mode': editorMode }">
      <draggable
        v-model="panels"
        item-key="id"
        class="dashboard-grid"
        :disabled="!editorMode"
        handle=".drag-handle"
      >
        <template #item="{ element: panel }">
          <div class="dashboard-item" :style="gridStyle(panel)">
            <div class="white-box" :class="panel?.class">
              <div class="white-box-top">
                <span v-if="editorMode" class="drag-handle">☰</span>
                <div v-if="editorMode" class="resize-controls">
                  <button
                    class="btn btn-small btn-secondary bg-danger text-white btn-no-r"
                    @click="removePanel(panel)"
                  >
                    ✕
                  </button>
                  <button
                    class="btn btn-small btn-secondary btn-no-r btn-no-l"
                    @click="resize(panel, -1)"
                  >
                    −
                  </button>
                  <button
                    class="btn btn-small btn-secondary btn-no-l"
                    @click="resize(panel, 1)"
                  >
                    +
                  </button>
                </div>
              </div>
              <div class="p-1">
                <component
                  :is="panel.component"
                  v-model="panel.data"
                  :appId="panel.id"
                  :key="panel.id + '-' + key"
                />
              </div>
            </div>
          </div>
        </template>
      </draggable>
      <section class="row" v-if="editorMode">
        <h5 class="mb-5">Add Panels</h5>
        <div
          class="dashboard-item col-4"
          v-for="panel in availablePanels"
          :key="panel.id"
        >
          <div class="white-box" :class="panel?.class">
            <div class="white-box-top z-index-1070">
              <div class="resize-controls">
                <button class="btn btn-sm btn-type-2" @click="addPanel(panel)">
                  <span>+</span>
                </button>
              </div>
            </div>
            <div class="p-1">
              <component
                :is="panel.component"
                v-model="panel.data"
                :appId="panel.id"
                :key="panel.id + '-' + key"
              />
            </div>
          </div>
        </div>

        <p v-if="availablePanels.length === 0" class="text-muted regular-14">
          All panels added
        </p>
      </section>
    </div>
  </div>
</template>

<script>
import { useStore } from "vuex";
import externalComponent from "../../modules/externalComponent";
import http from "../../modules/http";
import EditOnClick from "../../components/inputs/EditOnClick";
import { LineChart } from "vue-chart-3";
import draggable from "vuedraggable";
import { defineAsyncComponent } from "vue";

export default {
  name: "Home",
  components: { EditOnClick, LineChart, draggable },
  data() {
    return {
      store: useStore(),
      visible: [0],
      roles: [],
      isAdmin: false,
      allDashboards: [],
      dashboards: [],
      dashboard: null,
      editedName: "",
      panels: [],
      possiblePanels: {},
      appData: {},
      total: {},
      payments: [],
      switch2: "category",
      key: 0,
      editorMode: false,
      firstLoad: true,
      optionsNoText: {
        padding: 0,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
        },
        scales: {
          x: {
            display: false,
            grid: {
              display: false,
            },
          },
          y: {
            display: false,
            grid: {
              display: false,
            },
          },
        },
      },
      options: {
        padding: 0,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
        },
        scales: {
          x: {
            display: true,
            grid: {
              display: false,
            },
          },
          y: {
            display: false,
            grid: {
              display: false,
            },
          },
        },
      },
      revenueByDays: {
        labels: [],
        datasets: [
          {
            label: "Sold Products",
            data: [],
            lineTension: 0.45,
            borderColor: "rgba(160,85,224,1)",
            backgroundColor: "rgba(160,85,224,0.3)",
            fill: true,
          },
        ],
      },
      salesByDays: {
        labels: [],
        datasets: [
          {
            label: "Sales",
            data: [],
            lineTension: 0.45,
            borderColor: "rgba(160,85,224,1)",
            backgroundColor: "rgba(160,85,224,0.3)",
            fill: true,
          },
        ],
      },
      avgByDays: {
        labels: [],
        datasets: [
          {
            label: "Sales",
            data: [],
            lineTension: 0.45,
            borderColor: "rgba(160,85,224,1)",
            backgroundColor: "rgba(160,85,224,0.3)",
            fill: true,
          },
        ],
      },
    };
  },
  computed: {
    date() {
      return this.store.state.topBar.date;
    },
    warehouse() {
      return this.store.state.topBar.warehouse;
    },
    availablePanels() {
      const usedIds = this.panels.map((p) => p.id);

      return Object.values(this.possiblePanels).filter(
        (p) => !usedIds.includes(p.id)
      );
    },
  },
  mounted() {
    this.loadData();
    this.$nextTick(this.calculateHeights);
  },
  watch: {
    dashboard() {
      this.visible = this.dashboard.roles;
      this.loadDashboard();
    },
    editorMode() {
      if (this.editorMode) {
        http
          .fetch("/dashboard/1")
          .then((data) => {
            this.allDashboards = data;
            this.allDashboards.push({
              id: null,
              name: "➕ Create new view",
              isNew: true,
            });
          })
          .catch(() => {
            this.allDashboards = [];
          });
        http
          .fetch("/users/roles")
          .then((data) => {
            this.roles = data;
          })
          .catch(() => {
            this.roles = [];
          });
      }
    },
    date: {
      deep: true,
      handler() {
        this.loadData();
      },
    },
    warehouse() {
      this.loadData();
    },
  },
  methods: {
    percent(val) {
      return Math.round(val * 10000) / 100;
    },
    loadData() {
      http
        .fetch(
          `/home?1${this.warehouse ? "&warehouse=" + this.warehouse : ""}${
            (this.date[0]
              ? "&from=" + this.date[0].toISOString().split("T")[0]
              : "") +
            (this.date[1]
              ? "&to=" + this.date[1].toISOString().split("T")[0]
              : "")
          }`
        )
        .then(async (data) => {
          this.total = data.total;
          this.appData = data.apps;
          this.payments = data.payments;

          let labels = [];
          let revenue = [];
          let sales = [];
          let avg = [];
          for (const date in data.total.daily) {
            labels.push(date);
            revenue.push(Number.parseFloat(data.total.daily[date].total));
            sales.push(Number.parseFloat(data.total.daily[date].count));
            avg.push(Number.parseFloat(data.total.daily[date].avg));
          }
          this.revenueByDays.labels = labels;
          this.revenueByDays.datasets[0].data = revenue;
          this.salesByDays.labels = labels;
          this.salesByDays.datasets[0].data = sales;
          this.avgByDays.labels = labels;
          this.avgByDays.datasets[0].data = avg;
          this.dashboards = data.dashboards;
          this.dashboard = data.dashboards[0];
          this.isAdmin = data.is_admin;
          await this.loadPanels();
          if (this.firstLoad) {
            this.firstLoad = false;
          }
        });
    },
    gridStyle(panel) {
      return {
        gridColumn: `span ${panel.size || 6}`,
        gridRow: `span ${panel.rows || 20}`,
      };
    },
    panelClass(panel) {
      return panel.size ? "col-" + panel.size : "col-6";
    },
    calculateHeights() {
      console.log(document.querySelectorAll(".dashboard-item"));
      document.querySelectorAll(".dashboard-item").forEach((el, index) => {
        const height = el.firstChild.offsetHeight;
        const diff = height / 15;
        let rowSpan = Math.ceil(diff);
        const gap = rowSpan - diff;
        if (gap < 0.7) {
          rowSpan++;
        }
        console.log(height, rowSpan);
        this.panels[index].rows = rowSpan;
      });
    },

    resize(panel, delta) {
      panel.size = Math.min(12, Math.max(1, (panel.size || 6) + delta));
      this.$nextTick(this.calculateHeights);
    },
    changeSize(panel, delta) {
      panel.size = Math.min(12, Math.max(1, (panel.size || 6) + delta));
    },
    saveLayout() {
      let data = {
        id: this.dashboard.id ?? null,
        name: this.dashboard.name,
        blocks: [],
        visibility: this.visible,
      };
      for (const panel of this.panels) {
        data.blocks.push({
          block_name: panel.id,
          size: panel.size ?? 1,
          rows: panel.rows ?? 1,
        });
      }
      http
        .fetch("/dashboard/1", data)
        .then(() => {
          this.editorMode = false;
        })
        .catch((err) => console.error(err));
    },
    changeVisible(visible) {
      if (visible > 0) {
        this.visible = this.visible.filter((e) => e > 0);
      } else if (visible === 0) {
        this.visible = [0];
        this.selectedLineOption.visibility = 0;
      }
    },
    addPanel(panel) {
      this.panels.push({
        ...panel,
        size: panel.size ?? 6,
        rows: panel.rows ?? 20,
      });

      this.$nextTick(this.calculateHeights);
    },
    removePanel(panel) {
      this.panels = this.panels.filter((p) => p.id !== panel.id);
    },
    loadDashboard() {
      this.panels = [];
      for (const block of this.dashboard.blocks) {
        if (!this.possiblePanels[block.block_name]) {
          continue;
        }
        if (block.size) {
          this.possiblePanels[block.block_name].size = block.size;
        }
        if (block.rows) {
          this.possiblePanels[block.block_name].rows = block.rows;
        }
        this.panels.push(this.possiblePanels[block.block_name]);
      }
      setTimeout(() => {
        this.calculateHeights();
      }, 1000);
    },
    async loadPanels() {
      this.possiblePanels["Sales"] = {
        id: "Sales",
        app: null,
        component: defineAsyncComponent(() =>
          import(`../../components/home/Sales.vue`)
        ),
        data: {
          optionsNoText: this.optionsNoText,
          salesByDays: this.salesByDays,
          total: this.total,
        },
        name: "FulfillmentOrderCurrent",
        rows: 12,
        size: 4,
        title: "Sales number",
      };

      this.possiblePanels["Income"] = {
        id: "Income",
        app: null,
        component: defineAsyncComponent(() =>
          import(`../../components/home/Income.vue`)
        ),
        data: {
          optionsNoText: this.optionsNoText,
          revenueByDays: this.revenueByDays,
          total: this.total,
        },
        class: "white-box-big",
        name: "FulfillmentOrderCurrent",
        rows: 24,
        size: 5,
        title: "Sales value",
      };

      this.possiblePanels["Orders"] = {
        id: "Orders",
        app: null,
        component: defineAsyncComponent(() =>
          import(`../../components/home/Orders.vue`)
        ),
        data: {
          total: this.total,
        },
        name: "Orders number",
        rows: 12,
        size: 3,
        title: "Sales",
      };

      this.possiblePanels["AvgBasket"] = {
        id: "AvgBasket",
        app: null,
        component: defineAsyncComponent(() =>
          import(`../../components/home/AvgBasket.vue`)
        ),
        data: {
          optionsNoText: this.optionsNoText,
          avgByDays: this.avgByDays,
          total: this.total,
        },
        name: "Averige basket value",
        rows: 12,
        size: 4,
        title: "Sales",
      };

      this.possiblePanels["SuccessRate"] = {
        id: "SuccessRate",
        app: null,
        component: defineAsyncComponent(() =>
          import(`../../components/home/SuccessRate.vue`)
        ),
        data: {
          total: this.total,
        },
        name: "Payment success rate",
        rows: 12,
        size: 3,
        title: "Sales",
      };

      this.possiblePanels["PaymentsErrors"] = {
        id: "PaymentsErrors",
        app: null,
        component: defineAsyncComponent(() =>
          import(`../../components/home/PaymentsErrors.vue`)
        ),
        data: {
          total: this.total,
          payments: this.payments,
        },
        name: "Payment errors",
        rows: 12,
        size: 3,
        title: "Sales",
      };

      let _this = this;
      const apps = this.store.state.apps;
      const loaders = [];

      for (const key in apps) {
        if (!apps[key].homeComponents) continue;

        for (const compKey in apps[key].homeComponents) {
          const comp = apps[key].homeComponents[compKey];

          const panelId = `${apps[key].name}_${comp.name}`;

          comp.app = apps[key].name;
          comp.data = this.appData[apps[key].id];
          comp.id = panelId;

          if (this.firstLoad) {
            loaders.push(
              externalComponent(
                `/${apps[key].name}/home/${comp.name}.umd.min.js`
              ).then((MyComponent) => {
                comp.component = MyComponent;
                _this.possiblePanels[panelId] = comp;
              })
            );
          } else {
            // already loaded
            this.possiblePanels[panelId] = comp;
          }
        }
      }

      // wait for ALL components
      if (loaders.length) {
        await Promise.all(loaders);
      }
      this.loadDashboard();
    },
  },
};
</script>
<style scoped>
.editor-mode .white-box-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.editor-mode .white-box {
  user-select: none;
}
.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-auto-rows: 15px;
  grid-auto-flow: dense;
  gap: 0 1rem;
}

.dashboard-item {
  display: block;
}
.drag-handle {
  cursor: grab;
  font-size: 1.2rem;
  padding: 0.4rem 0.4rem 0 0.4rem;
}

.resize-controls {
  margin-top: 0.4rem;
  margin-right: 0.4rem;
}
</style>
